import { appStorage } from '../lib/betaStorage'
import { useEffect, useState } from 'react'
import { localISODate } from '../lib/date'
import { loadYouthSupportPlan } from '../lib/youthSupportPlan'

const STORAGE_PREFIX = 'schub.youthRegulation.'

type RegulationState = 'green' | 'amber' | 'red'

const states: { id: RegulationState; label: string; prompt: string; dotClass: string; className: string }[] = [
  { id: 'green', label: 'GO', prompt: 'Calm / Ready', dotClass: 'bg-emerald-500', className: 'border-emerald-500 bg-emerald-100 text-emerald-950' },
  { id: 'amber', label: 'SLOW DOWN', prompt: 'Anxious / Frustrated', dotClass: 'bg-amber-400', className: 'border-amber-500 bg-amber-100 text-amber-950' },
  { id: 'red', label: 'STOP', prompt: 'Too much / Explosive', dotClass: 'bg-red-500', className: 'border-red-500 bg-red-100 text-red-950' },
]

function stateKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}.${localISODate()}.state`
}

function choiceKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}.${localISODate()}.choice`
}

function loadState(userId: string): RegulationState | null {
  try {
    const value = appStorage.getItem(stateKey(userId))
    return value === 'green' || value === 'amber' || value === 'red' ? value : null
  } catch {
    return null
  }
}

export function YouthFeelingCheckIn({ userId }: { userId: string }) {
  const participantId = userId === 'u-jnr' ? 'u-jnr' : 'u-leo'
  const [selected, setSelected] = useState<RegulationState | null>(() => loadState(userId))
  const [supportChoice, setSupportChoice] = useState<string | null>(() => {
    try { return appStorage.getItem(choiceKey(userId)) } catch { return null }
  })
  const [strategies, setStrategies] = useState(() => loadYouthSupportPlan(participantId).regulationStrategies)

  useEffect(() => {
    const refresh = () => setStrategies(loadYouthSupportPlan(participantId).regulationStrategies)
    window.addEventListener('schub:youth-support-plan', refresh)
    return () => window.removeEventListener('schub:youth-support-plan', refresh)
  }, [participantId])

  const chooseState = (id: RegulationState) => {
    setSelected(id)
    setSupportChoice(null)
    try {
      appStorage.setItem(stateKey(userId), id)
      appStorage.removeItem(choiceKey(userId))
    } catch { /* prototype can still work without storage */ }
    window.dispatchEvent(new CustomEvent('schub:youth-regulation', { detail: { userId, state: id } }))
  }

  const chooseSupport = (id: string) => {
    setSupportChoice(id)
    try { appStorage.setItem(choiceKey(userId), id) } catch { /* prototype can still work without storage */ }
    window.dispatchEvent(new CustomEvent('schub:youth-regulation-support', { detail: { userId, state: selected, support: id, label: strategies.find((strategy) => strategy.id === id)?.label } }))
  }

  return (
    <section className="rounded-[2rem] bg-white p-5 shadow-card" aria-labelledby="youth-feeling-title">
      <div>
        <p className="text-sm font-extrabold text-violet-600">Check in</p>
        <h2 id="youth-feeling-title" className="mt-1 text-2xl font-black text-slate-900">How am I feeling today?</h2>
        <p className="mt-1 text-sm font-semibold text-slate-500">Choose the colour that feels right for your body.</p>
      </div>

      <div className="mt-4 grid grid-cols-3 items-stretch gap-3">
        {states.map((state) => {
          const active = selected === state.id
          return (
            <button
              key={state.id}
              type="button"
              aria-pressed={active}
              onClick={() => chooseState(state.id)}
              className={`grid min-h-40 grid-rows-[64px_44px_1fr] items-center rounded-[2rem] border-4 p-3 text-center transition active:scale-[0.97] ${state.className} ${active ? 'ring-4 ring-violet-300 ring-offset-2' : ''}`}
            >
              <span className={`mx-auto h-14 w-14 rounded-full border-4 border-white/80 shadow-sm ${state.dotClass}`} aria-hidden="true" />
              <span className="flex items-center justify-center text-base font-black leading-tight">{state.label}</span>
              <span className="flex items-start justify-center pt-1 text-[11px] font-extrabold leading-tight opacity-80">{state.prompt}</span>
            </button>
          )
        })}
      </div>

      {selected === 'green' && <div className="mt-4 rounded-2xl bg-emerald-50 p-4 text-center"><p className="text-sm font-black text-emerald-900">I feel ready. I can keep going at my pace.</p></div>}

      {(selected === 'amber' || selected === 'red') && (
        <div className={`mt-4 rounded-3xl p-4 ${selected === 'red' ? 'bg-red-50' : 'bg-amber-50'}`}>
          <p className="text-center text-base font-black text-slate-900">What might help right now?</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {strategies.map((choice) => (
              <button key={choice.id} type="button" aria-pressed={supportChoice === choice.id} onClick={() => chooseSupport(choice.id)} className={`min-h-20 rounded-2xl border-2 bg-white p-3 text-center shadow-sm active:scale-[0.98] ${supportChoice === choice.id ? 'border-violet-500 ring-2 ring-violet-200' : 'border-slate-200'}`}>
                <span className="block text-2xl" aria-hidden="true">{choice.icon}</span>
                <span className="mt-1 block text-sm font-black text-slate-800">{choice.label}</span>
              </button>
            ))}
          </div>
          {supportChoice && <p className="mt-4 text-center text-sm font-black text-violet-800">Great choice. I’m here with you.</p>}
        </div>
      )}

      <p className="mt-4 text-center text-xs font-semibold text-slate-500">No colour is bad. You can change your choice at any time.</p>
    </section>
  )
}

